import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'; // ✅ Only import ReactiveFormsModule here
import { ApiService } from '../../shared/services/api.service';
import { Subscriber, Subscription } from 'rxjs';
import { GstComponent } from '../gst/gst.component';
import { AddressComponent } from '../address/address.component';
import { Router } from '@angular/router';
import { ModalService } from '../../shared/services/modal.service';
import { LoaderService } from '../../shared/services/loader.service';
import { CookieService } from '../../shared/services/cookie.service';
import { LoginComponent } from '../login/login.component';
import { canAddGstDetails as canAddGstDetailsUtil } from '../../shared/utils/gst-eligibility';

interface Address {
  name: string;
  tag: string;
  address: string;
  contact?: string;
  gst?: string;
}

@Component({
  selector: 'app-user-address',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    GstComponent,
    AddressComponent,
    LoginComponent,
  ],
  templateUrl: './user-address.component.html',
  styleUrl: './user-address.component.scss',
})
export class UserAddressComponent {
  @Input() isStandalone: boolean = false;
  @Input() address: Address = {
    name: '',
    tag: '',
    address: '',
  };

  @Output() onEdit = new EventEmitter<void>();
  @Output() onRemove = new EventEmitter<void>();
  @Output() onSetAsDelivery = new EventEmitter<void>();
  @Output() onSetAsBilling = new EventEmitter<void>();

  addAdress: boolean = false;
  addGst: boolean = false;
  type = 'shipping';
  editingAddress: any = null;
  isEditMode: boolean = false;

  cardClasses = 'p-3 w-full rounded-lg bg-slate-50 max-md:max-w-full';
  headerContentClasses = 'flex gap-2 items-center self-stretch my-auto';
  nameClasses = 'self-stretch my-auto text-lg leading-none text-black';

  // Action button config
  editConfig = {
    icon: 'edit',
    label: 'Edit',
    variant: 'text',
  };

  isGSTEnabled: boolean = false;
  selectedType: string = 'Home';
  userData: any;

  addressForm!: FormGroup;
  sub: Subscription = new Subscription();
  curentRout: any;
  addresses: any;
  pincodeError: string = '';
  isPincodeLoading: boolean = false;
  pincodeSuccess: boolean = false;
  gstinError: string = '';
  isGstinLoading: boolean = false;
  gstinSuccess: boolean = false;
  stateCode: string = '';
  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private route: Router,
    private loaderService: LoaderService,
    private ssrcookieService: CookieService,
    private cd: ChangeDetectorRef,
    private modalService: ModalService,
  ) {}

  get hasSavedAddresses(): boolean {
    return !!(
      this.userData?.shipping_address ||
      this.userData?.billing_address ||
      (this.addresses && this.addresses.length > 0)
    );
  }

  get showAddressForm(): boolean {
    return !this.hasSavedAddresses;
  }

  get canAddGstDetails(): boolean {
    return canAddGstDetailsUtil(this.userData);
  }

  ngOnInit() {
    const storedUserData = localStorage.getItem('userData1');
    if (storedUserData) {
      this.userData = JSON.parse(storedUserData);
    }

    this.createForm();
    this.getAddressData();
    this.cd.detectChanges();
  }
  createForm() {
    this.addressForm = this.fb.group({
      gstin: [''],
      companyName: [''],
      receiverName: [''], // Remove initial validators - will be set conditionally
      receiverPhone: [''], // Remove initial validators - will be set conditionally
      address: ['', Validators.required],
      locality: ['', Validators.required],
      pin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      state: ['', Validators.required],
      city: ['', Validators.required],
      saveAs: ['', Validators.required], // Default to Work as shown in the image
      customSaveAs: [''], // Custom save as input for "Others" option
    });

    // Set initial validators based on GST toggle state after a small delay to ensure form is ready
    setTimeout(() => {
      this.updateGSTToggleValidators();
    }, 0);
  }

  toggleGST(enable: boolean) {
    if (enable && !this.canAddGstDetails) {
      this.isGSTEnabled = false;
      this.updateGSTToggleValidators();
      this.cd.detectChanges();
      return;
    }
    this.isGSTEnabled = enable;

    // Update form controls and validators
    this.updateGSTToggleValidators();

    // Force change detection
    this.cd.detectChanges();
  }

  private updateGSTToggleValidators() {
    if (!this.addressForm) {
      console.error('Address form not initialized');
      return;
    }

    const gstin = this.addressForm.get('gstin');
    const companyName = this.addressForm.get('companyName');
    const receiverName = this.addressForm.get('receiverName');
    const receiverPhone = this.addressForm.get('receiverPhone');
    const address = this.addressForm.get('address');
    const locality = this.addressForm.get('locality');
    const pin = this.addressForm.get('pin');
    const state = this.addressForm.get('state');
    const city = this.addressForm.get('city');

    if (this.isGSTEnabled) {
      // Enable and set validators for GST fields
      if (gstin) {
        gstin.enable();
        gstin.setValidators([Validators.required, this.gstinValidator()]);
      }

      if (companyName) {
        companyName.disable();
        companyName.setValidators([Validators.required]);
      }

      // Disable address-related fields when GST is enabled
      if (address) {
        address.disable();
      }
      if (locality) {
        locality.disable();
      }
      if (pin) {
        pin.disable();
      }
      if (state) {
        state.disable();
      }
      if (city) {
        city.disable();
      }

      // Clear validators and values for non-GST fields
      receiverName?.clearValidators();
      // receiverName?.setValue('');
      receiverPhone?.clearValidators();
      // receiverPhone?.setValue('');
    } else {
      // Disable and clear GST fields
      if (gstin) {
        gstin.disable();
        gstin.clearValidators();
        gstin.setValue('');
      }

      if (companyName) {
        companyName.disable();
        companyName.clearValidators();
        companyName.setValue('');
      }

      // Enable address-related fields when GST is disabled
      if (address) {
        address.enable();
      }
      if (locality) {
        locality.enable();
      }
      if (pin) {
        pin.enable();
      }
      if (state) {
        state.enable();
      }
      if (city) {
        city.enable();
      }

      // Set validators for receiver fields
      receiverName?.setValidators([Validators.required]);
      receiverPhone?.setValidators([
        Validators.required,
        Validators.pattern(/^[6-9]\d{9}$/),
        Validators.maxLength(10),
        Validators.minLength(10),
      ]);
    }

    // Update validity for all fields
    gstin?.updateValueAndValidity();
    companyName?.updateValueAndValidity();
    receiverName?.updateValueAndValidity();
    receiverPhone?.updateValueAndValidity();
    address?.updateValueAndValidity();
    locality?.updateValueAndValidity();
    pin?.updateValueAndValidity();
    state?.updateValueAndValidity();
    city?.updateValueAndValidity();
  }

  // Custom GSTIN validator
  private gstinValidator() {
    return (control: any) => {
      if (!control.value) {
        return null; // Don't validate empty values, let required handle that
      }

      const gstin = control.value.toUpperCase();

      // Check length
      if (gstin.length !== 15) {
        return { invalidGstin: 'GSTIN must be exactly 15 characters' };
      }

      // Check format: 2 digits, 5 uppercase letters, 4 digits, 1 uppercase letter, 1 digit/uppercase letter, 'Z', 1 digit/uppercase letter
      const gstinPattern =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstinPattern.test(gstin)) {
        return { invalidGstin: 'Please enter a valid GSTIN format' };
      }

      return null;
    };
  }

  saveAddress() {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }

    const formData = { ...this.addressForm.value };

    if (!this.isGSTEnabled) {
      delete formData.gstin;
      delete formData.companyName;
    } else {
      delete formData.receiverName;
      delete formData.receiverPhone;
    }
    // Send formData to server if needed
  }

  onSave(): void {
    // Add custom validation for customSaveAs when Others is selected
    if (this.addressForm.get('saveAs')?.value === 'Others') {
      const customSaveAsControl = this.addressForm.get('customSaveAs');
      if (
        !customSaveAsControl?.value ||
        customSaveAsControl.value.trim() === ''
      ) {
        customSaveAsControl?.setValidators([Validators.required]);
        customSaveAsControl?.updateValueAndValidity();
        customSaveAsControl?.markAsTouched();
      }
    } else {
      // Clear validation for customSaveAs when not Others
      const customSaveAsControl = this.addressForm.get('customSaveAs');
      customSaveAsControl?.clearValidators();
      customSaveAsControl?.updateValueAndValidity();
    }

    if (this.addressForm.valid) {
      if (this.addressForm.invalid) {
        this.addressForm.markAllAsTouched();
        return;
      }

      const formData = { ...this.addressForm.value };

      if (!this.isGSTEnabled) {
        delete formData.gstin;
        delete formData.companyName;
      } else {
        delete formData.receiverName;
        delete formData.receiverPhone;
      }

      // Determine the final address type
      const finalAddressType =
        formData.saveAs === 'Others' ? formData.customSaveAs : formData.saveAs;

      let payload: any = {
        address_1: formData.address,
        address_2: '',
        address_3: '',
        state: formData.state,
        city: formData.city,
        land_mark: formData.locality,
        pincode: formData?.pin || '',
        phone_no: this.userData?.phone_number,
        is_gstin_no: this.isGSTEnabled === true ? true : false,
        gstin_number: formData.gstin,
        address_type: finalAddressType,
        address_type_name:
          this.isGSTEnabled === true ? 'Billing Address' : 'Permanent Address',
        state_code: this.stateCode,
      };

      // Add type-specific fields
      if (this.isGSTEnabled) {
        // payload.receiver_name = formData.receiverName;
        // payload.receiver_phone_no = formData.receiverPhone;
        payload.company_name = formData.companyName;
      } else if (!this.isGSTEnabled) {
        payload.receiver_name = formData.receiverName;
        payload.receiver_phone_no = formData.receiverPhone;
        // payload.receiver_name = formData.receiverName;
      }
      this.loaderService.show();

      this.sub.add(
        this.apiService.addAddress(payload).subscribe({
          next: (res: any) => {
            if (res.success) {
              this.addressForm.reset();
              this.getAddressData();
              this.getUserDetails();
              this.loaderService.hide();
            } else {
              this.addressForm.reset();
              this.getAddressData();
              this.getUserDetails();
              this.loaderService.hide();
            }
          },
          error: (err) => {
            console.error('Error adding address:', err);
            this.loaderService.hide();
          },
        }),
      );
    } else {
      // Mark all fields as touched to show validation errors
      // this.mark  FormGroupTouched(this.addressForm);
      this.loaderService.hide();
    }
  }
  addressPopup(addressType: string) {
    this.type = addressType;
    this.addAdress = true;
    this.isEditMode = false;
    this.editingAddress = null;
  }

  editAddress(address: any, addressType: string) {
    this.editingAddress = address;
    this.type = addressType;
    this.isEditMode = true;
    this.addAdress = true;
  }

  gstPopup() {
    if (!this.canAddGstDetails) {
      return;
    }
    this.addGst = true;
  }

  selectAddressType(type: string) {
    this.selectedType = type;
  }

  onCloseBillingDetails() {
    this.addAdress = false;
    this.isEditMode = false;
    this.editingAddress = null;
  }
  onSavePopup() {
    this.addAdress = false;
    this.isEditMode = false;
    this.editingAddress = null;
    this.getAddressData();
    this.getUserDetails();
  }
  onCloseGSTINPopup() {
    this.addGst = false;
  }

  getAddressData() {
    this.loaderService.show();
    const payload = {
      offset: 0,
      limit: 10,
    };

    this.sub.add(
      this.apiService.getAddressData(payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.addresses = res.data;
          } else {
            this.addresses = [];
          }
          this.loaderService.hide();
        },
        error: (err) => {
          console.error('Error fetching address data:', err);
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      }),
    );
  }

  onSaveAddressDetails(event: any) {
    this.addAdress = false;
    this.isEditMode = false;
    this.editingAddress = null;

    // Refresh user data and address list
    this.getUserDetails();
    this.getAddressData();
  }

  onSaveGSTINDetails(event: any) {
    this.addGst = false;

    // this.getUserDetails(); // Refresh user data
  }

  // onSelect(data:any, action: any){

  // }

  onSelect(data: any, action: any) {
    const payload = {
      id: data.id, // this.selectedAddressId,
      shipping_address: action === 'delivery' ? true : '',
      billing_address: action === 'billing' ? true : '',
    };
    this.loaderService.show();
    this.sub.add(
      this.apiService.updateAdress(payload).subscribe({
        next: (res) => {
          this.getUserDetails();
          this.getAddressData();
          // this.closeAddressSelection();
          this.loaderService.hide();
        },
        error: (err) => {
          console.error('Error updating address:', err);
          this.loaderService.hide();
        },
      }),
    );
  }

  getUserDetails(): void {
    this.loaderService.show();
    const payload = {};
    this.sub.add(
      this.apiService.getUserDetails(payload).subscribe({
        next: (res) => {
          // Update localStorage and cookies with latest data
          localStorage.setItem('userData1', JSON.stringify(res?.data));
          this.ssrcookieService.setItem('eol_user', JSON.stringify(res?.data));
          this.userData = res?.data;
          this.loaderService.hide();
        },
        error: (err) => {
          console.error('Failed to fetch user details', err);
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      }),
    );
  }

  showDeleteAddressModal: boolean = false;
  addressIdToDelete: any = null;

  confirmRemoveAddress(id: any): void {
    this.addressIdToDelete = id;
    this.showDeleteAddressModal = true;
    this.modalService.openModal('delete-address-modal', () => this.cancelRemoveAddress());
  }

  cancelRemoveAddress(): void {
    this.showDeleteAddressModal = false;
    this.addressIdToDelete = null;
    this.modalService.closeModal('delete-address-modal');
  }

  executeRemoveAddress(): void {
    if (!this.addressIdToDelete) return;
    const id = this.addressIdToDelete;
    this.showDeleteAddressModal = false;
    this.addressIdToDelete = null;
    this.modalService.closeModal('delete-address-modal');
    this.removeAddress(id);
  }

  removeAddress(id: any) {
    const payload = {
      action_type: 'Remove Address',

      user_address_id: id,
    };
    this.loaderService.show();
    this.loaderService.show();

    this.sub.add(
      this.apiService.addAddress(payload).subscribe({
        next: (res) => {
          this.loaderService.hide();
          if (res.success) {
            this.loaderService.hide();
            this.getAddressData();
            if (
              id === this.userData.shipping_address.id ||
              id === this.userData.billing_address.id
            ) {
              this.getUserDetails();
            }
          } else {
            this.loaderService.hide();
            this.getAddressData();
          }
        },
        error: (err) => {
          console.error('Error removing address:', err);
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      }),
    );
  }

  onPincodeBlur(): void {
    const pincode = this.addressForm.get('pin')?.value;
    if (pincode && pincode.length === 6 && /^\d{6}$/.test(pincode)) {
      // Clear previous errors
      this.pincodeError = '';
      this.getAddressDataByPincode(pincode);
    } else if (pincode && pincode.length > 0) {
      this.pincodeError = 'Please enter a valid 6-digit PIN';
    }
  }

  onPincodeChange(): void {
    const pincode = this.addressForm.get('pin')?.value;

    // Clear previous error
    this.pincodeError = '';
    this.pincodeSuccess = false;
    this.isPincodeLoading = false;

    // Check if pincode is valid (6 digits)
    if (pincode && pincode.length === 6 && /^[0-9]{6}$/.test(pincode)) {
      this.getAddressDataByPincode(pincode);
    } else if (pincode && pincode.length > 0) {
      // Clear city and state if pincode is invalid or still being typed
      this.addressForm.patchValue({
        city: '',
        state: '',
      });
    } else {
      // Clear city and state when pincode is empty
      this.addressForm.patchValue({
        city: '',
        state: '',
      });
    }
  }

  // Method to allow only numbers in phone input
  onNumberOnly(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    // Allow: backspace, delete, tab, escape, enter
    if (
      [8, 9, 27, 13, 46].indexOf(charCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (charCode === 65 && event.ctrlKey === true) ||
      (charCode === 67 && event.ctrlKey === true) ||
      (charCode === 86 && event.ctrlKey === true) ||
      (charCode === 88 && event.ctrlKey === true)
    ) {
      return true;
    }
    // Ensure that it is a number and stop the keypress
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  // Method to handle paste events and allow only numbers
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text');
      // Remove all non-numeric characters
      const numericText = pastedText.replace(/[^0-9]/g, '');
      // Limit to 10 digits
      const limitedText = numericText.substring(0, 10);

      // Update the form control value
      const currentTarget = event.target as HTMLInputElement;
      if (
        currentTarget &&
        currentTarget.getAttribute('formControlName') === 'receiverPhone'
      ) {
        this.addressForm.patchValue({ receiverPhone: limitedText });
      }
    }
  }

  onGstinBlur(): void {
    const gstin = this.addressForm.get('gstin')?.value;

    // Auto-convert to uppercase
    if (gstin) {
      this.addressForm.patchValue(
        { gstin: gstin.toUpperCase() },
        { emitEvent: false },
      );
    }

    // Only fetch data if the format is valid
    if (gstin && this.addressForm.get('gstin')?.valid) {
      // Clear previous errors
      this.gstinError = '';
      this.getGstdata(gstin.toUpperCase());
    }
  }

  onGstinChange(): void {
    // Clear form fields when GSTIN changes
    this.addressForm.patchValue({
      companyName: '',
      address: '',
      locality: '',
      pin: '',
      city: '',
      state: '',
    });
    this.gstinError = '';
    this.isGstinLoading = false;
    this.gstinSuccess = false;
  }

  enableManualEdit(field: 'city' | 'state' | 'companyName' | 'address'): void {
    // Allow manual editing of auto-filled fields
    const control = this.addressForm.get(field);
    if (control) {
      control.enable();
    }
  }

  getAddressDataByPincode(pincode: string): void {
    this.loaderService.show();
    this.isPincodeLoading = true;
    this.pincodeError = '';

    const payload = {
      dropdown_type: 'get_data_by_pincode',
      search_pincode: pincode,
    };

    this.loaderService.show();
    this.sub.add(
      this.apiService.dataBypincode(payload).subscribe({
        next: (res: any) => {
          this.isPincodeLoading = false;

          if (res && res?.data && res?.data?.data_by_pincode?.length > 0) {
            const addressData = res?.data?.data_by_pincode[0]; // Get the first result

            // Auto-fill city and state - handle different possible field names
            const city = addressData.city_name || '';
            const state = addressData.state_name || '';
            this.stateCode = addressData.state_code || '';

            this.addressForm.patchValue({
              city: city,
              state: state,
            });

            // Clear any previous errors and set success
            this.pincodeError = '';
            this.pincodeSuccess = true;
          } else {
            this.pincodeError = 'No address found for this pincode';
            this.pincodeSuccess = false;
            // Clear city and state if no data found
            this.addressForm.patchValue({
              city: '',
              state: '',
            });
          }
          this.loaderService.hide();
        },
        error: (err) => {
          this.loaderService.hide();
          this.isPincodeLoading = false;
          console.error('Failed to fetch address data by pincode', err);
          this.pincodeError = 'Failed to fetch address data. Please try again.';
          this.pincodeSuccess = false;

          // Clear city and state on error
          this.addressForm.patchValue({
            city: '',
            state: '',
          });
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      }),
    );
  }

  getGstdata(number: string): void {
    this.loaderService.show();
    this.isGstinLoading = true;
    this.gstinError = '';

    if (!number || number.length !== 15) {
      console.error('Invalid GST number');
      this.gstinError = 'Please enter a valid 15-character GSTIN';
      this.isGstinLoading = false;
      this.loaderService.hide();
      return;
    }

    const payload = {
      gst_number: number,
    };

    this.sub.add(
      this.apiService.getGstdata(payload).subscribe({
        next: (res) => {
          this.isGstinLoading = false;

          if (res && res.data) {
            // Map API response to form fields
            this.addressForm.patchValue({
              companyName: res.data.company_name || '',
              address: res.data.address_1 || '',
              locality: res.data.locality || '',
              pin: res.data.pincode || '',
            });

            this.getAddressDataByPincode(res.data.pincode);

            // Clear any previous errors and set success
            this.gstinError = '';
            this.gstinSuccess = true;
          } else {
            this.gstinError = 'No company data found for this GSTIN';
            this.gstinSuccess = false;
          }
          this.loaderService.hide();
        },
        error: (err) => {
          this.isGstinLoading = false;
          console.error('Error fetching GST data:', err);
          this.gstinError = 'Failed to fetch company data. Please try again.';
          this.gstinSuccess = false;
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      }),
    );
  }

  preventLeadingSpace(event: KeyboardEvent, input: any) {
    const key = event.key;

    // Prevent space if it's the first character
    if (key === ' ' && (!input.value || input.selectionStart === 0)) {
      event.preventDefault();
      return;
    }

    // Allow letters, space, backspace, tab, and arrow keys
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', ' '];
    if (!/^[a-zA-Z]$/.test(key) && !allowedKeys.includes(key)) {
      event.preventDefault();
    }
  }

  preventLeading(event: KeyboardEvent, input: any) {
    const key = event.key;

    // Prevent space if it's the first character
    if (key === ' ' && (!input.value || input.selectionStart === 0)) {
      event.preventDefault();
      return;
    }

    // Allow letters, space, backspace, tab, and arrow keys
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', ' '];
    if (!/^[a-zA-Z0-9]$/.test(key) && !allowedKeys.includes(key)) {
      event.preventDefault();
    }
  }

  preventLeadingPincode(event: KeyboardEvent, input: any) {
    const key = event.key;

    // Prevent space if it's the first character
    if (key === ' ' && (!input.value || input.selectionStart === 0)) {
      event.preventDefault();
      return;
    }

    // Allow letters, space, backspace, tab, and arrow keys
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', ' '];
    if (!/^[0-9]$/.test(key) && !allowedKeys.includes(key)) {
      event.preventDefault();
    }
  }
}
